// src/components/Funnels/SkillToSaleQuizFunnel/SkillToSalesQuiz.tsx
import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import QuestionCard from "./QuestionCard";
import SimulationResults from "./SimulationResults";
// import SuccessScreen from "./SuccessScreen";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "./ProgressBar";
import { QUESTIONS } from "./quizQuestions";
import { QuizData, calculateSimulation } from "./quizLogic";

/* --- SkillToSalesQuiz (uses QuizData from quizLogic) --- */

const SkillToSalesQuiz: React.FC<{}> = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [quizData, setQuizData] = useState<QuizData>({
    knowledge: "",
    experience: "",
    audience: "",
    timeCommitment: "",
    currentIncome: "",
    goal: "",
    email: "",
    name: "",
  });
  const [simulation, setSimulation] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleNext = (): void => {
    if (!QUESTIONS) return;
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const sim = calculateSimulation(quizData);
      setSimulation(sim);
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    await new Promise((res) => setTimeout(res, 1500));
    setIsSubmitting(false);

    // navigate to success and pass name/email via location.state
    navigate("success", {
      state: {
        name: quizData.name || "",
        email: quizData.email || "",
      },
    });

    setCurrentStep((s) => s + 1);
  };

  const updateQuizData = (field: keyof QuizData, value: string): void => {
    setQuizData((prev) => ({ ...prev, [field]: value }));
  };

  if (currentStep === QUESTIONS.length && simulation) {
    return (
      <SimulationResults
        simulation={simulation}
        quizData={quizData}
        onNameChange={(name) => updateQuizData("name", name)}
        onEmailChange={(email) => updateQuizData("email", email)}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    );
  }

  if (currentStep < QUESTIONS.length) {
    const currentQuestion = QUESTIONS[currentStep];

    return (
      <div className="min-h-screen bg-brandNavy flex items-center justify-center py-12 px-4">
        <div className="max-w-3xl w-full">
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-extrabold text-brandWhite leading-tight">
              Skill → Sales: Quick Revenue Quiz
            </h1>
            <p className="mt-2 text-brandWhite/90">
              Answer a few simple questions to generate your Million-Naira
              Roadmap.
            </p>
          </div>

          <div
            className="bg-white rounded-2xl p-6 shadow-xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <ProgressBar
              currentStep={currentStep}
              totalSteps={QUESTIONS.length}
            />

            <QuestionCard
              question={currentQuestion}
              selectedValue={(quizData as any)[currentQuestion.id]}
              onSelect={(value) => updateQuizData(currentQuestion.id, value)}
            />

            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                onClick={handleNext}
                disabled={!(quizData as any)[currentQuestion.id]}
                className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition transform ${
                  (quizData as any)[currentQuestion.id]
                    ? "bg-brandRed text-brandWhite hover:bg-brandRedHover"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {currentStep === QUESTIONS.length - 1
                  ? "Generate My Million Naira Roadmap 🚀"
                  : "Continue"}
                <ChevronRight className="w-5 h-5 ml-3" />
              </button>

              <button
                onClick={() => {
                  if (currentStep > 0) setCurrentStep((s) => s - 1);
                }}
                className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm border border-lightBorder"
              >
                Back
              </button>
            </div>

            <div className="mt-4 text-center text-xs text-mutedGray">
              🔒 Your answers are private — we only use them to personalize your
              roadmap.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SkillToSalesQuiz;
